import { useMemo } from "react";

import clsx from "clsx";
import { sortBy } from "lodash";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { Spinner } from "@components/v2";
import { GET_UNIVERSAL_BENCHMARKS_CONVERSION_LAG_REPORT } from "@gql/universal-bentchmarks";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type GetUniversalBenchmarksConversionLagReport,
  type GetUniversalBenchmarksConversionLagReportVariables,
} from "@north-beam/nb-client-side";
import { formatting } from "@north-beam/nb-common";

import { useUniversalBenchmarksConfiguratorContext } from "../../hooks";

const COLORS = [
  "bg-v2-pluto/400",
  "bg-v2-earth/400",
  "bg-v2-sun/400",
  "bg-v2-mars/400",
];

export const ConversionLagReport = () => {
  const { user } = useUser();
  const { daysSelection, kpiAttributionModel, kpiAccountingMode } =
    useUniversalBenchmarksConfiguratorContext();

  const { data, loading } = useNorthbeamQuery<
    GetUniversalBenchmarksConversionLagReport,
    GetUniversalBenchmarksConversionLagReportVariables
  >(GET_UNIVERSAL_BENCHMARKS_CONVERSION_LAG_REPORT, {
    variables: {
      selectedDays: daysSelection
        .filter((e) => e.isExcluded || e.willBeExcluded)
        .map((e) => e.day),
      attributionModel: kpiAttributionModel,
      accountingMode: kpiAccountingMode,
    },
  });

  const conversionLagData = useMemo(() => {
    if (!data) {
      return null;
    }

    if (data.me.universalBenchmarksConversionLagReport.length === 0) {
      return null;
    }

    const rows = sortBy(data.me.universalBenchmarksConversionLagReport, [
      (e) => Number(e.attributionWindow),
    ]);

    const result = [];

    const threshold = Math.min(rows.length - 2, 3);
    const totalVal = rows[rows.length - 1].rev;
    const mainRows = rows.slice(0, threshold);
    const restRows = rows.slice(threshold);

    for (const [index, row] of mainRows.entries()) {
      const val = index === 0 ? row.rev : row.rev - mainRows[index - 1].rev;
      result.push({
        value: val,
        percent: val / totalVal,
        label: `${row.attributionWindow} Day`,
      });
    }

    const restVal = totalVal - mainRows[mainRows.length - 1].rev;
    const restLabel = `${restRows[0].attributionWindow}+ Day`;

    result.push({
      value: restVal,
      percent: restVal / totalVal,
      label: restLabel,
    });

    return result;
  }, [data]);

  return (
    <div className="border border-v2-tertiary rounded-lg p-4 h-[138px] flex flex-col justify-between">
      {loading ? (
        <div className="flex items-center justify-center h-full">
          <Spinner className="text-v2-cosmic/400" />
        </div>
      ) : (
        <>
          <div>
            <h3 className="text-base font-bold mb-0">Conversion Lag</h3>
            <p className="text-sm text-v2-secondary m-0">
              Days between media spend and revenue capture, in revenue.
            </p>
          </div>
          <div>
            <div className="flex items-center rounded overflow-hidden">
              {conversionLagData ? (
                conversionLagData.map((row, index) => {
                  return (
                    <GenericTooltip
                      key={row.label}
                      noInfoCircle
                      placement="top"
                      content={formatting.formatCurrency.approximate(
                        row.value,
                        { code: user.currencies.currentCurrency },
                      )}
                      className={clsx("h-3", COLORS[index])}
                      style={{ width: `${row.percent * 100}%` }}
                    ></GenericTooltip>
                  );
                })
              ) : (
                <div className="w-full h-3 bg-v2-gray/50"></div>
              )}
            </div>
          </div>
          <div
            className={clsx(
              "shrink-0 flex items-center justify-center gap-3 text-2xs text-v2-secondary",
            )}
          >
            {conversionLagData ? (
              <>
                {conversionLagData.map((row, index) => {
                  return (
                    <div key={row.label} className="flex items-center gap-1.5">
                      <span
                        className={clsx(
                          "inline-block w-2 h-2 rounded-full",
                          COLORS[index],
                        )}
                      ></span>
                      {row.label}
                    </div>
                  );
                })}
              </>
            ) : (
              <div></div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
